import { Box, Divider, Flex, FlexProps, Image, Spacer, Stack, Text } from "@chakra-ui/react";
import React, { FormEvent, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";

import { UserProfile as UserRole } from "@bcasmart/doxecure-commons/doxecureTypes";

import { NavLink } from "@components/NavLink/NavLink";
import { SearchField } from "@components/SearchField/SearchField";
import { UserProfile } from "@components/UserProfile/UserProfile";
import VersionDisplay from "@components/VersionDisplay";
import { config } from "@/config/config";
import { useAuthContext } from "@hooks/useAuthContext";
import { useLogout } from "@hooks/useLogout";
import { $g1Color, $g1IconURL } from "@/theme/G1Style";
import { trueOrEmptyCard } from "@utils/functions";
import { G1Icon } from "@utils/icons";

interface MenuContent {
    primary: MenuItem[];
    secondary: MenuItem[];
}

interface MenuItem {
    key: string;
    label: string;
    icon: React.ElementType;
    route: string;
}

export interface SidebarProps extends FlexProps {
    activeItemKey?: string;
    activesearch?: boolean;
    adminPage?: boolean;
    onSearch: (searchString: string) => void;
}

const adminMenuContent: MenuContent = {
    primary: [
        {
            key: "corporations",
            icon: G1Icon.Corporations,
            label: "Organisations",
            route: "/admin/corporations"
        },
        {
            key: "users",
            icon: G1Icon.Contacts,
            label: "Utilisateurs",
            route: "/admin/users"
        },
        {
            key: "consumptions",
            icon: G1Icon.Statistics,
            label: "Consommations",
            route: "/admin/consumptions"
        }
    ],
    secondary: []
} ;

const managerMenuContent: MenuContent = {
    primary: [
        {
            key: "corporations",
            icon: G1Icon.Corporations,
            label: "Organisation",
            route: "/admin/corporations"
        },
        {
            key: "users",
            icon: G1Icon.Contacts,
            label: "Collaborateurs",
            route: "/admin/users"
        },
        {
            key: "consumptions",
            icon: G1Icon.Statistics,
            label: "Consommations",
            route: "/admin/consumptions"
        }
    ],
    secondary: []
} ;

const appMenuContent: MenuContent = {
    primary: [
        {
            key: "signatures",
            icon: G1Icon.Signatures,
            label: "Parapheur",
            route: "/app/signatures",
        },
        {
            key: "newSignature",
            icon: G1Icon.NewSignature,
            label: "Mettre à la signature",
            route: "/app/newSignature",
        },
        {
            key: "draft",
            icon: G1Icon.Draft,
            label: "Brouillons",
            route: "/app/draft",
        },
        {
            key: "tobeSignedDocuments",
            icon: G1Icon.ToBeSignedDocuments,
            label: "En attente de signature",
            route: "/app/tobeSignedDocuments",
        },
        {
            key: "signedDocuments",
            icon: G1Icon.SignedDocuments,
            label: "Documents signés",
            route: "/app/signedDocuments",
        },
        {
            key: "refusedDocuments",
            icon: G1Icon.RefusedDocuments,
            label: "Documents refusés",
            route: "/app/refusedDocuments"
        },
        {
            key: "canceledDocuments",
            icon: G1Icon.CanceledDocuments,
            label: "Documents résiliés",
            route: "/app/canceledDocuments"
        },
        {
            key: "expiredDocuments",
            icon: G1Icon.ExpiredDocuments,
            label: "Documents expirés",
            route: "/app/expiredDocuments",
        },
        {
            key: "archivedDocuments",
            icon: G1Icon.ArchivedDocuments,
            label: "Documents archivés",
            route: "/app/archivedDocuments"
        }
    ],
    secondary: [
        {
            key: "contacts",
            icon: G1Icon.Contacts,
            label: "Contacts",
            route: "/app/contacts",
        }
    ],
};

const menuTobeApprovedItem = {
    key: "tobeApprovedDocuments",
    icon: G1Icon.ToBeApprovedDocuments,
    label: "En attente d'approbation",
    route: "/app/tobeApprovedDocuments",
};

if (config.app.templateManagement) {
    appMenuContent.secondary.push({
        key: "templates",
        icon: G1Icon.Templates,
        label: "Modèles",
        route: "/app/templates"
    }) ;
    appMenuContent.secondary.push({
        key: "newTemplate",
        icon: G1Icon.CreateTemplate,
        label: "Créer/Editer un modèle",
        route: "/app/newTemplate"
    }) ;
}

export const Sidebar = ({ ...props }: SidebarProps) => {
    const { activeItemKey, activesearch, adminPage, onSearch, ...rest } = props;
    const { isAdminAuthenticated, connectedUser, disconnect } = useAuthContext();
    const { mutateAsync: logout } = useLogout();
    const navigate = useNavigate();
    const menuContent = useMemo(() => {
        if (isAdminAuthenticated) {
            return connectedUser?.userFlags?.profile === UserRole.AdministratorCorporate ? managerMenuContent : adminMenuContent;
        }

        const userInCorporate = connectedUser?.userFlags?.profile === UserRole.AdministratorCorporate || connectedUser?.userFlags?.profile === UserRole.UserCorporate;
        let menu = appMenuContent;
        const approvedItemIndex = menu.primary.findIndex(item => item.key === 'tobeApprovedDocuments');
        if (userInCorporate && approvedItemIndex === -1) {
            const index = menu.primary.findIndex(item => item.key === 'tobeSignedDocuments');
            if (index !== -1) {
                menu.primary.splice(index, 0, menuTobeApprovedItem);
            }
        } else if (!userInCorporate && approvedItemIndex !== -1) {
            menu.primary.splice(approvedItemIndex, 1);
        }
        return menu;
    }, [isAdminAuthenticated, connectedUser?.userFlags?.profile]);

    const handleSearch = (e: FormEvent) => {
        e.preventDefault() ;
        if (e.type === "submit") {
            // @ts-ignore
            onSearch(e.target[0].value) ;
        }
    };

    async function _handleClickLogout(e) {
        e.preventDefault();
        disconnect();
        await logout() ;
        navigate(adminPage ? config.admin.loginURL! : config.app.loginURL!) ;
    }

    return (
        <Flex direction="column" borderRightWidth="1px" width="64" color={$g1Color("sidebar.write")} bg={$g1Color("sidebar.bg")} {...rest}>
            <Flex direction="column" flex="1" pt="5" pb="4" overflowY="auto" px="4">
                <Box key="logobox" mb="6" display="flex" justifyContent="space-between" alignItems="flex-end" color={$g1Color("sidebar.logo.write")} bg={$g1Color("sidebar.logo.bg")}>
                    <Stack direction="row" alignItems="center" spacing="5" key="logostack">
                        <Image src={$g1IconURL("sidebar.logo.iconurl")} key="logo" alt="Application logo" boxSize="48px" />
                        <Text>{adminPage ? "Administration de " : ""}{config.app.title}</Text>
                    </Stack>
                </Box>
                {!adminPage && !activesearch && (
                    <Box mb="6">
                        <form onSubmit={handleSearch}>
                            <SearchField component="sidebar" h="30px" />
                        </form>
                    </Box>
                )}

                <Stack spacing="6" as="nav" aria-label="Sidebar Navigation">
                    <Stack spacing="1">
                        {menuContent.primary.map((menuItem) => {
                            return (
                                <NavLink
                                    component="sidebar.menu"
                                    key={menuItem.key}
                                    label={menuItem.label}
                                    icon={menuItem.icon}
                                    to={menuItem.route}
                                    isActive={menuItem.key === activeItemKey}
                                />
                            );
                        })}
                    </Stack>
                    {trueOrEmptyCard(menuContent.secondary.length > 0,
                        <>
                            <Divider />

                            <Stack spacing="1">
                                {menuContent.secondary.map((menuItem) => {
                                    return (
                                        <NavLink
                                            component="sidebar.menu"
                                            key={menuItem.key}
                                            label={menuItem.label}
                                            icon={menuItem.icon}
                                            to={menuItem.route}
                                            isActive={menuItem.key === activeItemKey}
                                        />
                                    );
                                })}
                            </Stack>
                        </>)
                    }
                </Stack>
                <Spacer />
            </Flex>

            <Box position="relative">
                <VersionDisplay />
                {adminPage ? 
                    <UserProfile adminPage={true} logout={_handleClickLogout} />
                  : <Link to="/app/userProfile/account"><UserProfile logout={_handleClickLogout}/></Link>
                }    
            </Box>
        </Flex>
    );
};